import { useCallback, useEffect, useRef, useState } from "react";
import { getEncryptedMediaCacheKey } from "../utils/encryptedMedia";
import type { MatrixEncryptedFile } from "../utils/encryptedMedia";
import { getMatrixMediaBlob, getMatrixMediaSource } from "../utils/matrixMedia";
import type { MediaFetchOptions } from "../services/mediaCacheService";

interface UseAuthenticatedMediaResult {
    blobUrl: string;
    isLoading: boolean;
    error: string | null;
    retry: () => void;
}

export function useAuthenticatedMedia(
    src: string,
    encryptedFile?: MatrixEncryptedFile,
    mimeType?: string,
    lane?: MediaFetchOptions['lane']
): UseAuthenticatedMediaResult {
    const [blobUrl, setBlobUrl] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [retryCount, setRetryCount] = useState(0);
    const blobUrlRef = useRef('');
    const encryptedMediaKey = getEncryptedMediaCacheKey(encryptedFile, mimeType);
    const encryptedFileRef = useRef(encryptedFile);

    useEffect(() => {
        encryptedFileRef.current = encryptedFile;
    }, [encryptedMediaKey, encryptedFile]);

    useEffect(() => {
        if (!src) return;

        let cancelled = false;
        const abortController = lane === 'bulk' ? new AbortController() : undefined;

        const fetchMedia = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const currentEncryptedFile = encryptedFileRef.current;
                const mediaMxc = getMatrixMediaSource(src, currentEncryptedFile);

                if (!mediaMxc.startsWith('mxc://')) {
                    setBlobUrl(mediaMxc);
                    blobUrlRef.current = '';
                    setIsLoading(false);
                    return;
                }

                const blob = abortController
                    ? await getMatrixMediaBlob(src, currentEncryptedFile, mimeType, { lane, signal: abortController.signal })
                    : await getMatrixMediaBlob(src, currentEncryptedFile, mimeType);

                if (cancelled) return;

                // Revoke previous blob URL if any
                if (blobUrlRef.current) {
                    URL.revokeObjectURL(blobUrlRef.current);
                }

                const url = URL.createObjectURL(blob);
                blobUrlRef.current = url;
                setBlobUrl(url);
            } catch (err) {
                if (cancelled) return;
                setError(err instanceof Error ? err.message : 'Failed to load media');
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        fetchMedia();

        return () => {
            cancelled = true;
            abortController?.abort();
            if (blobUrlRef.current) {
                URL.revokeObjectURL(blobUrlRef.current);
                blobUrlRef.current = '';
                setBlobUrl('');
            }
        };
    }, [src, encryptedMediaKey, mimeType, retryCount, lane]);

    const retry = useCallback(() => {
        setRetryCount(count => count + 1);
    }, []);

    return { blobUrl, isLoading, error, retry };
}
